import React, { useState } from 'react';
import { RefreshCw, Database, ChevronRight, Info } from 'lucide-react';

const ErrorRecoveryCard = ({ 
  errorRecovery, 
  onTableSelectionRequested,
  onRetryRequested 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!errorRecovery || !errorRecovery.conversation) {
    return null;
  }

  const { conversation, error_analysis } = errorRecovery;
  const { title, message, icon, suggested_tables, original_query, original_question } = conversation;

  const handleTableSelectionClick = () => {
    onTableSelectionRequested({
      suggestedTables: suggested_tables,
      originalQuery: original_query,
      originalQuestion: original_question,
      errorContext: error_analysis
    });
  };

  const getErrorTypeColor = (errorType) => {
    switch (errorType) {
      case 'null_result':
        return 'bg-yellow-50 border-yellow-200';
      case 'sql_execution_error':
        return 'bg-red-50 border-red-200';
      case 'connection_error':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getErrorTypeIcon = (errorType) => {
    switch (errorType) {
      case 'null_result':
        return '🔍';
      case 'sql_execution_error':
        return '⚠️';
      case 'connection_error':
        return '🔌';
      default:
        return '❓';
    }
  };

  return (
    <div className="border-2 rounded-lg p-4 bg-gray-800/50 border-gray-600">{/* Dark theme styling */}
      {/* Header */}
      <div className="flex items-start space-x-3 mb-3">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-xl">{icon}</span>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-gray-100 mb-1">{title}</h3>
          <p className="text-sm text-gray-300">{message}</p>
        </div>
      </div>

      {/* Error Analysis Summary */}
      {error_analysis && (
        <div className="mb-4 p-3 bg-gray-700/30 rounded-lg border border-gray-600">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm">{getErrorTypeIcon(error_analysis.error_type)}</span>
            <span className="text-sm font-medium text-gray-200">
              {error_analysis.error_type.replace('_', ' ').toUpperCase()}
            </span>
            {error_analysis.is_recoverable && (
              <span className="px-2 py-1 text-xs bg-green-900/30 text-green-300 rounded-full border border-green-600">
                Recoverable
              </span>
            )}
          </div>
          
          {error_analysis.user_guidance && (
            <div className="text-sm text-gray-300 mb-2">
              <strong>Issue:</strong> {error_analysis.user_guidance.message}
            </div>
          )}
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-200"
          >
            <Info className="w-3 h-3" />
            <span>{isExpanded ? 'Hide' : 'Show'} technical details</span>
            <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
          
          {isExpanded && (
            <div className="mt-2 p-2 bg-gray-800/50 rounded text-xs text-gray-300">
              <div className="mb-1"><strong>Original Query:</strong> {original_query}</div>
              <div className="mb-1"><strong>Recovery Strategy:</strong> {error_analysis.recovery_strategy}</div>
              {error_analysis.next_steps && error_analysis.next_steps.length > 0 && (
                <div>
                  <strong>Next Steps:</strong>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    {error_analysis.next_steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Suggested Tables Preview */}
      {suggested_tables && suggested_tables.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-200 mb-2">Suggested Tables:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {suggested_tables.slice(0, 4).map((table, index) => (
              <div key={table.table_name || index} className="p-2 bg-gray-700/30 rounded border border-gray-600">
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-200">{table.table_name || 'Unknown Table'}</span>
                  <span className="text-xs text-gray-400">({table.row_count || 0} rows)</span>
                </div>
                <p className="text-xs text-gray-300 mt-1">{table.description || 'No description available'}</p>
                {table.match_reasons && table.match_reasons.length > 0 && (
                  <div className="text-xs text-gray-400 mt-1">
                    Reason: {table.match_reasons[0]}
                  </div>
                )}
              </div>
            ))}
          </div>
          {suggested_tables.length > 4 && (
            <div className="text-xs text-gray-400 mt-2">
              +{suggested_tables.length - 4} more tables available
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleTableSelectionClick}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <Database className="w-4 h-4" />
          <span>Select Tables</span>
        </button>
        
        {onRetryRequested && (
          <button
            onClick={onRetryRequested}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorRecoveryCard;